import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Avatar, Box, Chip, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import { getToken } from "../services/auth";

const PAGE_SIZE = 20;

export default function FeedbackHub() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const sentinel = useRef(null);

  const loadPage = useCallback(async (nextPage) => {
    setLoading(true); setError("");
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/feedback?page=${nextPage}&pageSize=${PAGE_SIZE}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Unable to load feedback");
      setItems((current) => nextPage === 1 ? data.items : [...current, ...data.items]);
      setPage(data.page); setTotal(data.total); setHasMore(data.hasMore);
    } catch (loadError) { setError(loadError.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadPage(1); }, [loadPage]);
  useEffect(() => {
    const target = sentinel.current;
    if (!target) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMore && !loading && page > 0) loadPage(page + 1);
    }, { rootMargin: "240px" });
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, loadPage, loading, page]);

  return <Box sx={{ px: { xs: 1, md: 3 }, pb: 7, maxWidth: 1100, mx: "auto" }}>
    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={2} sx={{ mb: 3 }}>
      <Box><Typography variant="h4" sx={{ fontWeight: 850, color: "#12324f" }}>Feedback Hub</Typography><Typography color="text.secondary">Recent ideas and observations from application users.</Typography></Box>
      <Chip icon={<ForumRoundedIcon />} label={`${total} active feedback item${total === 1 ? "" : "s"}`} color="primary" variant="outlined" />
    </Stack>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    <Stack spacing={1.5}>
      {items.map((item) => <Paper key={item.id} variant="outlined" sx={{ p: 2.25, borderRadius: 3, borderColor: "#dce7f1" }}>
        <Stack direction="row" spacing={1.6} alignItems="flex-start">
          <Avatar sx={{ bgcolor: "#e5f3fa", color: "#087d91", fontWeight: 800 }}>{item.userName?.charAt(0)?.toUpperCase()}</Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}><Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={.5}><Typography sx={{ fontWeight: 750 }}>{item.userName} <Typography component="span" variant="body2" color="text.secondary">({item.username})</Typography></Typography><Typography variant="caption" color="text.secondary">{new Intl.DateTimeFormat("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" }).format(new Date(item.createdAt))} IST</Typography></Stack><Typography sx={{ mt: 1, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{item.message}</Typography></Box>
        </Stack>
      </Paper>)}
      {!loading && !items.length && !error && <Paper variant="outlined" sx={{ py: 9, textAlign: "center", borderRadius: 3 }}><ForumRoundedIcon sx={{ fontSize: 48, color: "text.disabled" }} /><Typography color="text.secondary" sx={{ mt: 1 }}>No active feedback yet.</Typography></Paper>}
      <Box ref={sentinel} sx={{ height: 2 }} />
      {loading && <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}><CircularProgress size={28} /></Box>}
      {!hasMore && items.length > 0 && <Typography align="center" variant="body2" color="text.secondary">All feedback loaded</Typography>}
    </Stack>
  </Box>;
}
